import { Helmet } from "react-helmet-async";

interface SEOHeadProps {
  title: string;
  description: string;
  ogImage?: string;
  canonicalUrl: string;
  type?: "website" | "article";
  structuredData?: object | object[];
  noindex?: boolean;
  puzzleNumber?: number;
  guessCount?: number;
}

export const SEOHead = ({
  title,
  description,
  ogImage = "/og/wordpop-share.png",
  canonicalUrl,
  type = "website",
  structuredData,
  noindex = false,
  puzzleNumber,
  guessCount,
}: SEOHeadProps) => {
  const fullTitle = title.includes("WordPop") ? title : `${title} | WordPop`;
  const fullOgImage = ogImage.startsWith("http") ? ogImage : `https://wordpop.games${ogImage}`;
  const fullCanonicalUrl = canonicalUrl.startsWith("http") ? canonicalUrl : `https://wordpop.games${canonicalUrl}`;

  // Generate puzzle-specific OG tags if puzzle data is provided
  const ogTitle = puzzleNumber ? `WordPop — Puzzle #${puzzleNumber}` : fullTitle;
  const ogDescription = puzzleNumber && guessCount 
    ? `My results for Puzzle #${puzzleNumber}: ${guessCount}/5` 
    : description;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullCanonicalUrl} />

      {/* Open Graph Tags */}
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={ogDescription} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:url" content={fullCanonicalUrl} />
      <meta property="og:site_name" content="WordPop" />

      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullOgImage} />

      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(Array.isArray(structuredData) ? structuredData : [structuredData])}
        </script>
      )}
    </Helmet>
  );
};